/**
 * Created by Florin David on 19/10/2016.
 */

import { InitialState } from 'initialState'
import { TimeRange } from 'search/filters'
import { LogEventCount, TimeInterval } from 'search/chart'
import { Log } from 'types/search/logs'
import { ApiKey } from 'types/settings/api-keys'

/**
 * Should keep constants for several reasons
 * - avoid duplicates
 * - start developing from reducers level
 * - dispatch several redux actions from a single action
 */

// App constants
export const RESET_STATE = 'RESET_STATE'
export const SET_TRANSLATION = 'SET_TRANSLATION'
export const SET_GENERIC_APP_ERROR = 'SET_GENERIC_APP_ERROR'
export const CLEAR_GENERIC_APP_ERROR = 'CLEAR_GENERIC_APP_ERROR'
export const SET_UPDATE_IN_PROGRESS = 'SET_UPDATE_IN_PROGRESS'
export const SET_UPDATE_READY = 'SET_UPDATE_READY'
export const SET_OFFLINE = 'SET_OFFLINE'
export const SET_SMALL_SCREEN_SIZE = 'SET_SMALL_SCREEN_SIZE'

export const SET_LANGUAGE_LIST = 'SET_LANGUAGE_LIST'
export const SET_SELECTED_LANGUAGE = 'SET_SELECTED_LANGUAGE'
export const UPDATE_TRANSLATION = 'UPDATE_TRANSLATION'
export const SET_FETCHING_TRANSLATION = 'SET_FETCHING_TRANSLATION'

// Auth constants
export const SET_USER_TOKEN = 'SET_USER_TOKEN'
export const SET_USERNAME = 'SET_USERNAME'
export const SET_FETCHING_LOGIN = 'SET_FETCHING_LOGIN'
export const SET_LOGIN_ERROR = 'SET_LOGIN_ERROR'
export const CLEAR_LOGIN_ERROR = 'CLEAR_LOGIN_ERROR'
export const LOGOUT = 'LOGOUT'

// Attributes constants
export const SET_ATTRIBUTES = 'SET_ATTRIBUTES'
export const SET_ATTRIBUTES_TREE = 'SET_ATTRIBUTES_TREE'
export const SET_FETCHING_ATTRIBUTES = 'SET_FETCHING_ATTRIBUTES'
export const SET_SELECTED_ATTRIBUTE = 'SET_SELECTED_ATTRIBUTE'
export const SET_ATTRIBUTE_VALUES = 'SET_ATTRIBUTE_VALUES'
export const SET_FETCHING_ATTRIBUTE_VALUES = 'SET_FETCHING_ATTRIBUTE_VALUES'

// Filters constants
export const SET_TIME_RANGE = 'SET_TIME_RANGE'
export const SET_DATE_FILTER = 'SET_DATE_FILTER'
export const ADD_ATTRIBUTE_VALUE_FILTER = 'ADD_ATTRIBUTE_VALUE_FILTER'
export const REMOVE_ATTRIBUTE_VALUE_FILTER = 'REMOVE_ATTRIBUTE_VALUE_FILTER'
export const CLEAR_ATTRIBUTE_VALUE_FILTERS = 'CLEAR_ATTRIBUTE_VALUE_FILTERS'

// Chart constants
export const SET_LOG_EVENTS_COUNT_DATA = 'SET_LOG_EVENTS_COUNT_DATA'
export const SET_FETCHING_LOG_EVENTS_COUNT_DATA =
  'SET_FETCHING_LOG_EVENTS_COUNT_DATA'

// Search UI constants
export const TRIGGER_MANUAL_SEARCH = 'TRIGGER_MANUAL_SEARCH'

// API Keys constants
export const SET_API_KEYS = 'SET_API_KEYS'
export const SET_FETCHING_API_KEYS = 'SET_FETCHING_API_KEYS'
export const SET_CREATING_API_KEY = 'SET_CREATING_API_KEY'

// Logs constants (search/logs)
export const SET_LOGS_DATA = 'SET_LOGS_DATA'
export const APPEND_LOGS_DATA = 'APPEND_LOGS_DATA'
export const SET_LOGS_NEXT_TOKEN_DATA = 'SET_LOGS_NEXT_TOKEN_DATA'
export const SET_FETCHING_LOGS_DATA = 'SET_FETCHING_LOGS_DATA'
export const SET_FETCHING_MORE_LOGS_DATA = 'SET_FETCHING_MORE_LOGS_DATA'
export const CLEAR_LOGS_DATA = 'CLEAR_LOGS_DATA'

export interface ActionsPayloadsMap {
  [RESET_STATE]: InitialState
  [SET_TRANSLATION]: Translation
  [SET_GENERIC_APP_ERROR]: GenericError
  [CLEAR_GENERIC_APP_ERROR]: undefined
  [SET_UPDATE_IN_PROGRESS]: boolean
  [SET_UPDATE_READY]: boolean
  [SET_OFFLINE]: boolean
  [SET_SMALL_SCREEN_SIZE]: boolean

  // Language
  [SET_LANGUAGE_LIST]: Language[]
  [SET_SELECTED_LANGUAGE]: string
  [SET_FETCHING_TRANSLATION]: boolean

  // Auth
  [SET_USER_TOKEN]: string | null
  [SET_USERNAME]: string | null
  [SET_FETCHING_LOGIN]: boolean
  [SET_LOGIN_ERROR]: string | null
  [CLEAR_LOGIN_ERROR]: undefined
  [LOGOUT]: undefined

  // Attributes
  [SET_ATTRIBUTES]: unknown[]
  [SET_ATTRIBUTES_TREE]: unknown[]
  [SET_FETCHING_ATTRIBUTES]: boolean
  [SET_SELECTED_ATTRIBUTE]: string | null
  [SET_ATTRIBUTE_VALUES]: unknown[]
  [SET_FETCHING_ATTRIBUTE_VALUES]: boolean

  // Filters
  [SET_TIME_RANGE]: TimeRange
  [SET_DATE_FILTER]: { startDate: string | null; endDate: string | null }
  [ADD_ATTRIBUTE_VALUE_FILTER]: { attributeCode: string; value: string }
  [REMOVE_ATTRIBUTE_VALUE_FILTER]: { attributeCode: string; value: string }
  [CLEAR_ATTRIBUTE_VALUE_FILTERS]: undefined

  // Chart
  [SET_LOG_EVENTS_COUNT_DATA]: {
    data: LogEventCount[]
    interval: TimeInterval
  }
  [SET_FETCHING_LOG_EVENTS_COUNT_DATA]: boolean

  // Search UI
  [TRIGGER_MANUAL_SEARCH]: string // ISO timestamp

  // API Keys
  [SET_API_KEYS]: ApiKey[]
  [SET_FETCHING_API_KEYS]: boolean
  [SET_CREATING_API_KEY]: boolean

  // Logs (search/logs)
  [SET_LOGS_DATA]: Log[]
  [APPEND_LOGS_DATA]: Log[]
  [SET_LOGS_NEXT_TOKEN_DATA]: string | null
  [SET_FETCHING_LOGS_DATA]: boolean
  [SET_FETCHING_MORE_LOGS_DATA]: boolean
  [CLEAR_LOGS_DATA]: undefined
}

export type ActionCreator<T extends ActionsPayloadsMap> = {
  [key in keyof T]: {
    type: key
    payload: T[key]
  }
}[keyof T]

type Action = ActionCreator<ActionsPayloadsMap>

export default Action
